// src/components/shared/CourseCard/CourseCard.tsx
import UltraCompactCard from './variants/UltraCompactCard';
import PlaceholderCard from './variants/PlaceholderCard';
import SuggestionCard, { type SuggestionCardProps, type SuggestionCourse } from './variants/SuggestionCard';
import { useCourseCard, type CourseCardProps } from './hooks/useCourseCard';

export type CourseCardComponentProps = CourseCardProps &
  Pick<
    SuggestionCardProps,
    | 'onAcceptSuggestion'
    | 'onRejectSuggestion'
    | 'onReassignGenEd'
    | 'isAcceptingSuggestion'
    | 'isRejectingSuggestion'
  >;

/**
 * CourseCard component — routes to the appropriate variant based on course type.
 *
 * - Suggestions → SuggestionCard (accept/reject UI)
 * - Placeholders → PlaceholderCard (edit/convert/delete UI)
 * - Regular courses → UltraCompactCard (expandable planning card)
 */
const CourseCard = (props: CourseCardComponentProps) => {
  const { course, requirementTags, customActions, semester } = props;

  const cardProps = useCourseCard(props);

  if (course?.isSuggestion) {
    return (
      <SuggestionCard
        course={course as unknown as SuggestionCourse}
        semester={semester}
        courseRoles={props.courseRoles}
        onAcceptSuggestion={props.onAcceptSuggestion}
        onRejectSuggestion={props.onRejectSuggestion}
        onReassignGenEd={props.onReassignGenEd}
        isAcceptingSuggestion={props.isAcceptingSuggestion}
        isRejectingSuggestion={props.isRejectingSuggestion}
        checkCourseAvailability={props.checkAvailability ?? undefined}
      />
    );
  }

  if (course?.isPlaceholder) {
    return (
      <PlaceholderCard
        {...cardProps}
        requirementTags={requirementTags}
        customActions={customActions}
        semester={semester}
      />
    );
  }

  return (
    <UltraCompactCard
      {...cardProps}
      requirementTags={requirementTags}
      customActions={customActions}
      semester={semester}
      onReassignGenEd={props.onReassignGenEd}
    />
  );
};

export default CourseCard;
